import { createFileRoute, notFound } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { BlogPostView } from "@/components/blog/BlogPostView";
import { getPostBySlug } from "@/lib/blog.functions";
import { parseYouTubeId } from "@/lib/youtube";
import { canonical } from "@/lib/seo";
import { creditNodes } from "@/lib/attribution";
import { ogImageMeta } from "@/lib/og-image";
import { videoSchema } from "@/lib/video-schema";
import { cdnImageUrl } from "@/lib/image-cdn";

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params, context }) => {
    const data = await context.queryClient.fetchQuery({
      queryKey: ["blog", "post", params.slug],
      queryFn: async () => {
        const res = await getPostBySlug({ data: { slug: params.slug } });
        if (!res) throw notFound();
        return res;
      },
    });
    return data;
  },
  component: PostRoute,
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [{ title: "Article unavailable | Durafoam Roofing" }, { name: "robots", content: "noindex" }, { name: "description", content: "This Durafoam roofing article is unavailable." }, { property: "og:title", content: "Article unavailable | Durafoam Roofing" }, { property: "og:description", content: "This Durafoam roofing article is unavailable." }, { property: "og:type", content: "article" }, { name: "twitter:card", content: "summary_large_image" }] };
    const post = loaderData.post;
    const path = `/blog/${params.slug}`;
    const title = post.meta_title ?? post.title;
    const desc =
      post.meta_description ??
      post.excerpt ??
      "Practical Phoenix roofing advice from Durafoam Roofing — foam roofs, repairs, recoating, and costs, explained by a family-owned crew serving the Valley since 1989.";
    const authorNodes =
      creditNodes(post.authors) ??
      (post.author === "Tim Forstie"
        ? [{ "@id": "https://www.durafoaminc.com/tim-forstie#person" }]
        : [{ "@type": "Organization", name: "Durafoam Roofing" }]);
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      datePublished: post.published_at,
      dateModified: post.updated_at,
      author: authorNodes,
      contributor: creditNodes(post.contributors),
      reviewedBy: creditNodes(post.reviewers),
      publisher: {
        "@type": "Organization",
        name: "Durafoam Roofing",
        logo: { "@type": "ImageObject", url: "https://www.durafoaminc.com/logo.png" },
      },
      image: post.featured_image_url ?? undefined,
      url: canonical(path),
    };
    const faqSchema =
      post.faq_items.length > 0
        ? {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: post.faq_items.map((f) => ({
              "@type": "Question",
              name: f.question,
              acceptedAnswer: { "@type": "Answer", text: f.answer },
            })),
          }
        : null;

    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: canonical("/") },
        { "@type": "ListItem", position: 2, name: "Blog", item: canonical("/blog") },
        ...(post.category_id && post.category_name
          ? [
              {
                "@type": "ListItem",
                position: 3,
                name: post.category_name,
                item: canonical(`/blog/category/${post.category_id}`),
              },
              { "@type": "ListItem", position: 4, name: post.title, item: canonical(path) },
            ]
          : [{ "@type": "ListItem", position: 3, name: post.title, item: canonical(path) }]),
      ],
    };
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "article" },
        { property: "og:url", content: canonical(path) },
        ...ogImageMeta(
          post.og_image_url ??
            (post.featured_image_url
              ? cdnImageUrl(post.featured_image_url, { width: 1200, height: 630, quality: 72, format: "webp", resize: "cover" })
              : "/og/og-blog.png"),
        ),
      ],
      links: [{ rel: "canonical", href: post.canonical_url ?? canonical(path) }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(articleSchema) },
        { type: "application/ld+json", children: JSON.stringify(breadcrumb) },
        ...(faqSchema
          ? [{ type: "application/ld+json", children: JSON.stringify(faqSchema) }]
          : []),
        ...(() => {
          const vid = parseYouTubeId(post.youtube_url);
          if (!vid) return [];
          return [
            {
              type: "application/ld+json",
              children: JSON.stringify(
                videoSchema({
                  name: post.title,
                  description: post.meta_description ?? post.excerpt ?? post.title,
                  youtubeId: vid,
                  uploadDate: post.published_at ?? post.created_at,
                  thumbnailUrl: post.video_thumbnail_url ?? undefined,
                  contentUrl: post.youtube_url ?? undefined,
                }),
              ),
            },
          ];
        })(),
      ],
    };
  },
  errorComponent: ({ error }) => (
    <div className="p-12 text-center">
      <p>Could not load post.</p>
      <pre className="mt-2 text-xs">{error instanceof Error ? error.message : String(error)}</pre>
    </div>
  ),
  notFoundComponent: () => (
    <div className="p-12 text-center">
      <h1 className="text-2xl font-bold">Post not found</h1>
    </div>
  ),
});

function PostRoute() {
  const { slug } = Route.useParams();
  const fn = useServerFn(getPostBySlug);
  const { data } = useSuspenseQuery({
    queryKey: ["blog", "post", slug],
    queryFn: () => fn({ data: { slug } }),
  });
  if (!data) return null;
  return <BlogPostView post={data.post} related={data.related} />;
}
